import { useNavigate } from '@tanstack/react-router';
import { ERefundOption, TRefundOption } from '../lib/types';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';
import { useState } from 'react';
import RefundByCheckDialog from './RefundByCheckDialog';
import { UseQueryResult } from '@tanstack/react-query';
import { IPaymentSettings } from '../services/payment-settings';
import { Skeleton } from './ui/skeleton';
import PaymentNotAvailableHoverCard from './PaymentNotAvailableHoverCard';
import { useTranslation } from 'react-i18next';

type TRefundOptionParams = TRefundOption & {
  globalPaymentSettings: UseQueryResult<IPaymentSettings, Error>;
  facilityPaymentSettings: UseQueryResult<IPaymentSettings, Error>;
  claimTypeSettings: 'emailPaymentMethods' | 'letterPaymentMethods';
  refundAmount: number;
};

const RefundOptionCard = ({
  heading,
  title,
  time,
  description,
  action,
  isRecommended,
  path,
  globalPaymentSettings,
  facilityPaymentSettings,
  refundMethod,
  claimTypeSettings,
  refundAmount,
  limit,
}: TRefundOptionParams) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const toggleIsOpen = () => setIsOpen(!isOpen);

  const onClick = () => {
    if (title === ERefundOption.CHECK_MAIL) {
      toggleIsOpen();
      return;
    }

    navigate({
      to: path,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        paymentType: title,
        step: 2,
      }),
    });
  };

  const isNotAvailable =
    !globalPaymentSettings.data?.[claimTypeSettings].includes(refundMethod);

  const isOverLimit = limit && refundAmount > limit;

  if (globalPaymentSettings.isLoading || facilityPaymentSettings.isLoading)
    return (
      <Skeleton className='h-80 w-full border-primary-700 shadow-card-shadow lg:w-[260px]' />
    );

  return (
    <>
      <RefundByCheckDialog
        isOpen={isOpen}
        toggleIsOpen={toggleIsOpen}
        navigateToCheck={() =>
          navigate({
            to: path,
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              paymentType: title,
              step: 2,
            }),
          })
        }
      />
      <PaymentNotAvailableHoverCard isAvailable={!isNotAvailable}>
        {facilityPaymentSettings.data?.[claimTypeSettings].includes(
          refundMethod,
        ) && (
          <Card
            className={cn(
              'relative flex h-full w-full flex-col shadow-card-shadow transition-shadow duration-300 hover:shadow-xl lg:w-[260px]',
              isOverLimit
                ? 'border-2 border-dashed border-orange-700 dark:border-orange-200'
                : isNotAvailable
                  ? 'border-2 border-dashed border-red-700'
                  : 'border border-primary-700',
              (isNotAvailable || isOverLimit) &&
                'cursor-not-allowed opacity-50',
            )}
          >
            {(isNotAvailable || isOverLimit) && (
              <div
                className={cn(
                  'absolute right-[-2px] top-[-2px] rounded-bl-md rounded-tr-md px-2 py-1 text-xs font-extrabold text-card shadow-lg',
                  isOverLimit
                    ? 'bg-orange-600 dark:bg-orange-300'
                    : 'bg-red-500',
                )}
              >
                {isOverLimit ? t('Above Dollar Limit') : t('Not available')}
              </div>
            )}

            <CardHeader>
              <CardDescription
                className={cn(
                  'mb-2 w-fit rounded-md px-2 py-1 text-sm md:mb-4',
                  isRecommended
                    ? 'bg-primary-600 text-primary-100'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {t(heading)}
              </CardDescription>
              <CardTitle className='text-2xl'>{t(title)}</CardTitle>
            </CardHeader>
            <CardContent className='flex-grow'>
              <h5
                className={cn(
                  'mb-2 font-semibold',
                  isOverLimit
                    ? 'text-orange-700 dark:text-orange-200'
                    : isNotAvailable
                      ? 'text-red-700'
                      : 'text-primary-600',
                )}
              >
                {t(time)}
              </h5>
              <p className='text-md text-muted-foreground md:h-20'>
                {t(description)}
              </p>
            </CardContent>
            <CardFooter className='flex items-center justify-center'>
              {isOverLimit ? (
                <div className='w-full rounded-lg border border-orange-700 p-3 dark:border-orange-200'>
                  <p className='text-center text-xs font-medium text-orange-700 dark:text-orange-200'>
                    {t('Amount Exceeds')} ${limit}
                  </p>
                </div>
              ) : isNotAvailable ? (
                <div className='w-full rounded-lg border border-red-700 p-3 dark:border-red-200'>
                  <p className='text-center text-xs font-medium text-red-700 dark:text-red-200'>
                    {t('Not available')}
                  </p>
                </div>
              ) : (
                <Button
                  variant='outline'
                  className={cn('w-full')}
                  disabled={isNotAvailable}
                  onClick={onClick}
                >
                  {t(action)}
                </Button>
              )}
            </CardFooter>
          </Card>
        )}
      </PaymentNotAvailableHoverCard>
    </>
  );
};

export default RefundOptionCard;
